import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { useRole } from "@/lib/roleContext";
import { useAuth } from "@/lib/authContext";
import {
    ArrowLeft, ArrowUp, ArrowDown, Minus, TrendingUp, Users, Ticket,
    CalendarDays, MapPin, Globe2, ShieldAlert, Sparkles, AlertCircle,
} from "lucide-react";
import { formatDate } from "@/lib/format";

const ALLOWED = new Set(["artist_owner", "artist_team", "admin"]);

function usePerfAccess() {
    const { role, activeArtistId } = useRole();
    const { artists } = useAuth();
    const membership = (artists || []).find((a) => a.id === activeArtistId);
    const membershipRole = membership?.role;
    // Membership is authoritative; global role stays "fan" for new artist owners.
    return role === "admin" || ALLOWED.has(role) || ["owner", "manager", "team"].includes(membershipRole);
}

export default function EventPerformance() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { role } = useRole();
    const hasAccess = usePerfAccess();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        endpoints.eventPerformance(id).then((d) => {
            setData(d);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [id]);

    if (!hasAccess) {
        return (
            <div className="p-8 md:p-16" data-testid="perf-forbidden">
                <div className="border border-[#ff3333] p-8 max-w-2xl">
                    <div className="flex items-center gap-2 text-[#ff3333]">
                        <ShieldAlert className="w-5 h-5" />
                        <span className="font-mono uppercase tracking-widest text-xs">Access denied</span>
                    </div>
                    <h1 className="mt-3 font-display font-black uppercase text-2xl">
                        Performance reports are for artist workspace roles only
                    </h1>
                    <p className="mt-3 text-sm text-[#a3a3a3]">
                        Switch your role to Artist Owner or Team Member to view this report.
                    </p>
                </div>
            </div>
        );
    }

    if (loading) return <div className="p-6 font-mono text-sm text-[#666]" data-testid="perf-loading">Loading performance…</div>;
    if (!data) return null;

    if (!data.is_completed) {
        return (
            <div className="p-6 md:p-10" data-testid="perf-not-completed">
                <button onClick={() => navigate("/workspace/events")} className="text-[10px] font-mono uppercase text-[#666] hover:text-[#ff4400] flex items-center gap-1">
                    <ArrowLeft className="w-3 h-3" /> Events
                </button>
                <div className="mt-4 border border-[#ffea00] p-6 max-w-2xl">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#ffea00]">Not completed yet</div>
                    <h1 className="mt-2 font-display font-black uppercase text-2xl">
                        {data.event.name}
                    </h1>
                    <p className="mt-3 text-sm text-[#a3a3a3]">
                        The performance report becomes available once this event's date has passed.
                    </p>
                </div>
            </div>
        );
    }

    const k = data.kpis;
    const c = data.comparison;

    return (
        <div className="pm-fade" data-testid="event-performance-page">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6">
                <button onClick={() => navigate("/workspace/events")} className="text-[10px] font-mono uppercase text-[#666] hover:text-[#ff4400] flex items-center gap-1" data-testid="perf-back">
                    <ArrowLeft className="w-3 h-3" /> Events
                </button>
                <div className="mt-1 flex items-baseline gap-3">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">Performance · {formatDate(data.event.date)}</div>
                </div>
                <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl leading-tight" data-testid="perf-event-name">
                    {data.event.name}
                </h1>
                <div className="mt-2 text-xs text-[#a3a3a3] flex flex-wrap gap-3">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {data.event.venue_name} · {data.event.city}, {data.event.country}</span>
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> Capacity {data.event.capacity || "—"}</span>
                </div>
            </header>

            {/* Summary lines */}
            {data.summary_lines.length > 0 && (
                <section className="px-4 md:px-8 py-5 border-b border-[#2a2a2a]" data-testid="perf-summary">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#666] mb-2 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> Summary
                    </div>
                    <ul className="space-y-1">
                        {data.summary_lines.map((line, i) => (
                            <li key={i} className="text-sm md:text-base" data-testid={`perf-summary-line-${i}`}>
                                — {line}
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* KPI grid */}
            <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[#2a2a2a] border-b border-[#2a2a2a]">
                <KpiCard label="Registrations" value={k.total_registrations} testId="kpi-registrations" />
                <KpiCard label="Attendance" value={k.actual_attendance} highlight="orange" testId="kpi-attendance" />
                <KpiCard label="Attendance rate" value={`${k.attendance_rate}%`} testId="kpi-rate" />
                <KpiCard label="Capacity used" value={`${k.capacity_utilization}%`} testId="kpi-capacity" />
                <KpiCard label="Walk-ins" value={k.walkins} testId="kpi-walkins" />
                <KpiCard label="No-shows" value={k.no_shows} testId="kpi-noshows" />
                <KpiCard label="New fans" value={k.new_fans_acquired} testId="kpi-new-fans" />
                <KpiCard label="Returning fans" value={k.returning_fans} testId="kpi-returning" />
                <KpiCard label="Returning rate" value={`${k.returning_fan_rate}%`} testId="kpi-returning-rate" />
                <KpiCard label="Identifiable" value={k.identifiable_attendees} testId="kpi-identifiable" />
                <KpiCard label="Capacity" value={k.capacity || "—"} testId="kpi-capacity-total" />
                <KpiCard
                    label="Followers gained"
                    value={data.followers_growth?.available ? data.followers_growth.window_total : "—"}
                    sub={data.followers_growth?.available
                        ? `${data.followers_growth.direct_event_follows} direct from this event`
                        : (data.followers_growth?.message || "Insufficient data")}
                    highlight={data.followers_growth?.available && data.followers_growth.window_total > 0 ? "orange" : undefined}
                    testId="kpi-followers"
                />
            </section>

            {/* Comparison */}
            <section className="px-4 md:px-8 py-6 border-b border-[#2a2a2a]" data-testid="perf-comparison">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">vs previous event</div>
                {c ? (
                    <>
                        <div className="mt-1 text-sm text-[#a3a3a3]">
                            Compared to <Link to={`/workspace/events/${c.previous_event.id}/performance`} className="text-[#ff4400] hover:underline" data-testid="perf-prev-link">{c.previous_event.name}</Link> ({formatDate(c.previous_event.date)})
                        </div>
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                            <DeltaCard label="Attendance" delta={c.actual_attendance} testId="delta-attendance" />
                            <DeltaCard label="Attendance rate" delta={c.attendance_rate} suffix="%" testId="delta-rate" />
                            <DeltaCard label="Capacity used" delta={c.capacity_utilization} suffix="%" testId="delta-capacity" />
                            <DeltaCard label="New fans" delta={c.new_fans_acquired} testId="delta-new-fans" />
                            <DeltaCard label="Returning rate" delta={c.returning_fan_rate} suffix="%" testId="delta-returning" />
                        </div>
                    </>
                ) : (
                    <div className="mt-3 border border-[#2a2a2a] p-4 flex items-start gap-2" data-testid="perf-comparison-empty">
                        <AlertCircle className="w-4 h-4 text-[#a3a3a3] mt-0.5" />
                        <div className="text-sm text-[#a3a3a3]">
                            This is your first completed event — nothing to compare against yet.
                        </div>
                    </div>
                )}
            </section>

            {/* Breakdowns */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#2a2a2a] border-b border-[#2a2a2a]">
                <Panel title="Ticket / access breakdown" kicker="Attendance by tier" testId="perf-ticket-breakdown">
                    {k.ticket_breakdown.length === 0 ? (
                        <Empty text="No attendees checked in yet." />
                    ) : (
                        <BarList items={k.ticket_breakdown.map((t) => ({ key: t.name, label: t.name, value: t.count }))} />
                    )}
                </Panel>
                <Panel title="Payment status" kicker="Checked-in totals" testId="perf-payment-totals">
                    {k.payment_totals.length === 0 ? (
                        <Empty text="No checked-in attendees to summarize." />
                    ) : (
                        <BarList items={k.payment_totals.map((t) => ({ key: t.status, label: statusLabel(t.status), value: t.count }))} />
                    )}
                </Panel>
            </section>

            <section className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#2a2a2a] border-b border-[#2a2a2a]">
                <Panel title="Fans by city" kicker={<span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> location</span>} testId="perf-by-city">
                    {k.by_city.length === 0 ? (
                        <Empty text="No identifiable fan location data yet." />
                    ) : (
                        <BarList items={k.by_city.map((t) => ({ key: t.city, label: t.city, value: t.count }))} />
                    )}
                </Panel>
                <Panel title="Fans by country" kicker={<span className="flex items-center gap-1"><Globe2 className="w-3 h-3" /> reach</span>} testId="perf-by-country">
                    {k.by_country.length === 0 ? (
                        <Empty text="No identifiable fan country data yet." />
                    ) : (
                        <BarList items={k.by_country.map((t) => ({ key: t.country, label: t.country, value: t.count }))} />
                    )}
                </Panel>
            </section>

            {data.followers_growth?.available ? (
                <section className="px-4 md:px-8 py-6 border-b border-[#2a2a2a]" data-testid="perf-follower-growth">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#666] flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" /> Follower growth · {data.followers_growth.measurement_window}
                    </div>
                    <div className="mt-1 text-xs text-[#a3a3a3]">
                        Window: <span className="text-[#f4f4f0] font-mono">{data.followers_growth.window_start} → {data.followers_growth.window_end}</span>
                    </div>
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                        <KpiCard label="Before event" value={data.followers_growth.before} sub="7 days prior" testId="fg-before" />
                        <KpiCard label="Event day" value={data.followers_growth.on_event_day} sub="0-24h of event date" testId="fg-day" />
                        <KpiCard label="After event" value={data.followers_growth.after} sub="7 days after" testId="fg-after" />
                        <KpiCard label="Window total" value={data.followers_growth.window_total} highlight="orange" testId="fg-total" />
                        <KpiCard label="Net change" value={data.followers_growth.net_change} sub={`${data.followers_growth.unfollows_in_window} unfollow${data.followers_growth.unfollows_in_window === 1 ? "" : "s"}`} testId="fg-net" />
                        <KpiCard label="Direct from event" value={data.followers_growth.direct_event_follows} sub={`${data.followers_growth.direct_event_follow_percentage ?? 0}% of window`} testId="fg-direct" />
                    </div>
                    <div className="mt-4 text-xs text-[#a3a3a3] max-w-3xl leading-relaxed" data-testid="perf-follower-caveat">
                        <span className="text-[#f4f4f0]">{data.followers_growth.window_total}</span> follower{data.followers_growth.window_total === 1 ? "" : "s"} gained during the event window.{" "}
                        <span className="text-[#f4f4f0]">{data.followers_growth.direct_event_follows}</span> ({data.followers_growth.direct_event_follow_percentage ?? 0}%) originated directly from this event page.
                        Correlation with the event is not necessarily causation for follows outside the direct-attribution set.
                    </div>
                </section>
            ) : (
                <div className="px-4 md:px-8 py-4 border-b border-[#2a2a2a] text-xs font-mono uppercase text-[#666] flex items-start gap-2" data-testid="perf-followers-insufficient">
                    <AlertCircle className="w-4 h-4 text-[#666] mt-0.5" />
                    {data.followers_growth?.message || "Follower growth unavailable."}
                </div>
            )}
        </div>
    );
}

function statusLabel(s) {
    return { paid: "Paid", unpaid: "Unpaid", pay_at_door: "Pay at door" }[s] || s;
}

function KpiCard({ label, value, sub, highlight, testId }) {
    const color = highlight === "orange" ? "text-[#ff4400]" : "text-[#f4f4f0]";
    return (
        <div className="bg-[#0a0a0a] p-4 md:p-5" data-testid={testId}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">{label}</div>
            <div className={`mt-2 font-display font-black uppercase tracking-tighter text-2xl md:text-3xl ${color}`}>{value}</div>
            {sub && <div className="text-[10px] font-mono uppercase text-[#a3a3a3] mt-1">{sub}</div>}
        </div>
    );
}

function DeltaCard({ label, delta, suffix = "", testId }) {
    const diff = delta.diff;
    const up = diff > 0;
    const down = diff < 0;
    const Icon = up ? ArrowUp : down ? ArrowDown : Minus;
    const color = up ? "text-[#00e676]" : down ? "text-[#ff3333]" : "text-[#a3a3a3]";
    return (
        <div className="bg-[#0a0a0a] p-4" data-testid={testId}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">{label}</div>
            <div className="mt-1 font-display font-black text-xl md:text-2xl">{delta.current}{suffix}</div>
            <div className={`mt-1 flex items-center gap-1 text-xs font-mono uppercase ${color}`}>
                <Icon className="w-3 h-3" />
                {up ? "+" : ""}{diff}{suffix}
                {delta.pct !== null && ` (${delta.pct > 0 ? "+" : ""}${delta.pct}%)`}
            </div>
            <div className="text-[10px] font-mono uppercase text-[#a3a3a3]">was {delta.previous}{suffix}</div>
        </div>
    );
}

function Panel({ title, kicker, testId, children }) {
    return (
        <div className="bg-[#0a0a0a] p-6" data-testid={testId}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">{kicker}</div>
            <div className="mt-1 mb-4 font-display font-black uppercase tracking-tighter text-xl md:text-2xl">{title}</div>
            {children}
        </div>
    );
}

function BarList({ items }) {
    const max = Math.max(...items.map((i) => i.value)) || 1;
    return (
        <div className="space-y-2">
            {items.map((it) => (
                <div key={it.key} data-testid={`bar-${it.key}`}>
                    <div className="flex items-center justify-between text-xs font-mono uppercase">
                        <span className="text-[#a3a3a3]">{it.label}</span>
                        <span className="text-[#f4f4f0]">{it.value}</span>
                    </div>
                    <div className="h-1.5 bg-[#141414] mt-1"><div className="h-full bg-[#ff4400]" style={{ width: `${(it.value / max) * 100}%` }} /></div>
                </div>
            ))}
        </div>
    );
}

function Empty({ text }) {
    return <div className="text-sm text-[#a3a3a3]">{text}</div>;
}
